import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

type Tone = 'danger' | 'warning' | 'info';

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (value: boolean) => {
    setOpen(false);
    resolverRef.current?.(value);
    resolverRef.current = null;
  };

  const tone: Tone = opts?.tone ?? 'warning';
  const iconColor =
    tone === 'danger' ? 'text-red-400' : tone === 'warning' ? 'text-amber-400' : 'text-cubus-cyan';
  const iconBg =
    tone === 'danger' ? 'bg-red-500/10' : tone === 'warning' ? 'bg-amber-500/10' : 'bg-cubus-cyan/10';
  const confirmVariant = tone === 'danger' ? 'danger' : 'lime';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal
          open={open}
          onClose={() => close(false)}
          size="sm"
          hideClose
          ariaLabel={opts.title}
          footer={
            <>
              <Button variant="ghost" size="md" onClick={() => close(false)}>
                {opts.cancelText ?? 'Cancelar'}
              </Button>
              <Button variant={confirmVariant} size="md" onClick={() => close(true)} autoFocus>
                {opts.confirmText ?? 'Confirmar'}
              </Button>
            </>
          }
        >
          <div className="flex gap-4">
            <div className={`shrink-0 w-10 h-10 rounded-full ${iconBg} flex items-center justify-center`}>
              <AlertTriangle className={`w-5 h-5 ${iconColor}`} aria-hidden="true" />
            </div>
            <div className="flex-1 pt-0.5">
              <h3 className="text-base font-semibold text-white">{opts.title}</h3>
              {opts.description && <div className="mt-1 text-sm text-muted">{opts.description}</div>}
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider />');
  }
  return fn;
}
